/**
 * Drag and drop in a vertical list: the arithmetic shared by the storyline (its boxes, and the menu on the
 * left that is dragged into it) and the storyboard (its manual cues). The browser hands the panels the
 * pointer and the boxes' bounds; these helpers turn that into an insertion point and apply the drop, so
 * the panels only render the bar and call them.
 *
 * An insertion point is a number 0..n: "before box i", with n meaning "after the last box".
 */

/** The insertion point under the pointer: before the first box whose middle is below it, else past the end. */
export function insertionAt(y: number, boxes: ReadonlyArray<{ top: number; bottom: number }>): number {
  const i = boxes.findIndex((b) => y < (b.top + b.bottom) / 2);
  return i < 0 ? boxes.length : i;
}

/** Dropping the item at `from` on insertion point `to` leaves the list as it is. */
export const sameSpot = (from: number, to: number): boolean => to === from || to === from + 1;

/** The index the item at `from` has after a drop on insertion point `to` (counted before the move). */
export const movedIndex = (from: number, to: number): number => (sameSpot(from, to) ? from : from < to ? to - 1 : to);

/** Move the item at `from` to insertion point `to`, both counted before the move; a copy of the list when nothing changes. */
export function moveTo<T>(list: readonly T[], from: number, to: number): T[] {
  const next = [...list];
  if (from < 0 || from >= next.length || to < 0 || to > next.length || sameSpot(from, to)) return next;
  const [x] = next.splice(from, 1);
  next.splice(movedIndex(from, to), 0, x!);
  return next;
}

/** Insert `x` at insertion point `at`, clamped to the list. */
export function insertAt<T>(list: readonly T[], at: number, x: T): T[] {
  const next = [...list];
  next.splice(Math.max(0, Math.min(next.length, at)), 0, x);
  return next;
}

/**
 * A list whose tail may be reordered while its head keeps its order (the storyboard: cues anchored to the
 * script come as the script says, the manual ones after them, in the order the editor gives them).
 * The index where the tail starts, or -1 when there is none.
 */
export function tailStart<T>(list: readonly T[], isTail: (x: T) => boolean): number {
  return list.findIndex(isTail);
}

/** The insertion point a drop in such a list snaps to: never inside the head. */
export function tailSpot<T>(list: readonly T[], isTail: (x: T) => boolean, at: number): number {
  const first = tailStart(list, isTail);
  return first < 0 ? list.length : Math.max(first, Math.min(list.length, at));
}

/** Move a tail item at `from` to insertion point `at` (snapped into the tail); null when `from` is not in the tail. */
export function moveWithinTail<T>(list: readonly T[], isTail: (x: T) => boolean, from: number, at: number): T[] | null {
  const first = tailStart(list, isTail);
  if (first < 0 || from < first || from >= list.length || !isTail(list[from]!)) return null;
  return moveTo(list, from, tailSpot(list, isTail, at));
}
